import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";

const FIELDS = [["name", "Franchise Partner Name *", 2], ["contact_person", "Contact Person"], ["contact", "Contact Number"], ["email", "Email"],
  ["pan", "PAN", 1, 10], ["gstin", "GSTIN", 1, 15], ["city", "City"], ["state", "State"], ["address", "Address", 2], ["remarks", "Remarks", 2]];

export const EditFranchiseDialog = ({ franchise, open, onClose, onSaved }) => {
  const { user } = useAuth();
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && franchise) setF({ ...franchise, verification_status: franchise.verification_status || "Pending" }); }, [open, franchise]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    if (!(f.name || "").trim()) return toast.error("Franchise Partner Name is required");
    setBusy(true);
    const body = Object.fromEntries(FIELDS.map(([k]) => [k, (f[k] || "").trim()]));
    body.status = f.status;
    if (user?.is_super) body.verification_status = f.verification_status;
    try {
      const { data } = await api.put(`/franchise/${franchise.id}`, body);
      toast.success("Franchise details saved");
      if (data?.certificate) toast.success(`Certificate ${data.certificate.certificate_number} issued`);
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-card" data-testid="edit-franchise-dialog">
        <DialogHeader><DialogTitle className="font-head">Edit Franchise · {franchise?.referral_number}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {FIELDS.map(([k, label, span = 1, max]) => (
            <div key={k} className={span === 2 ? "col-span-2" : ""}><Label>{label}</Label>
              <Input value={f[k] || ""} maxLength={max} onChange={(e) => set(k, max ? e.target.value.toUpperCase() : e.target.value)} data-testid={`edit-franchise-${k}`} /></div>
          ))}
          <div><Label>Status</Label>
            <Select value={f.status || "Active"} onValueChange={(v) => set("status", v)}><SelectTrigger data-testid="edit-franchise-status"><SelectValue /></SelectTrigger>
              <SelectContent>{["Active", "Inactive", "Suspended"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Verification</Label>
            <Select value={f.verification_status} onValueChange={(v) => set("verification_status", v)} disabled={!user?.is_super}>
              <SelectTrigger data-testid="edit-franchise-verification"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Verified">Verified</SelectItem></SelectContent></Select>
            <p className="mt-1 text-[11px] text-muted-foreground">{user?.is_super ? "" : "Only the Super Admin can change this. "}{franchise?.verified_by ? `Last changed by ${franchise.verified_by} on ${fmtDate(franchise.verified_at)}.` : ""}</p>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">An issued certificate keeps the details it was issued with. Reissue it to show updated details or the Verified badge.</p>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="edit-franchise-save">Save Changes</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
